import { useContext, useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import {
  BILLING_QUERY_KEY,
  getBilling,
  startCheckout,
  verifyPayment,
  type BillingView,
  type PaymentAttempt,
} from '../api/endpoints/planPayment';
import { startStripeCheckout, type StripeCheckoutInput } from '../api/endpoints/stripe';
import { SessionContext } from '../auth/SessionContext';
import { ErrorBanner } from '../components/ErrorBanner';
import { LoadingState } from '../components/LoadingState';
import { PagoMethods } from '../components/PagoMethods';
import { ResultPanel } from '../components/PagoResultPanel';
import { CardLocked, CardTrial, StripeReturn } from '../components/PagoStripe';
import { PagoSummary } from '../components/PagoSummary';
import { formatWholeDollars } from '../components/PlanPicker';
import { attemptNotice, isTrustedCheckoutUrl, METHOD_COPY, paidUntilLine } from '../lib/planPayment';
import { planLabel } from '../lib/plan';
import { isCardLockedNow, isTrialRunning } from '../lib/trial';
import { useMutationAttempt } from '../lib/use-mutation-attempt';
import { SignupSteps } from '../components/SignupSteps';
import '../styles/signup.css';
import '../styles/pago.css';

/**
 * Pago: the owner pays their Dale plan, inside the product. Reached right
 * after signup (step 3 of Plan · Tu negocio · Pago) and from Membresía's
 * "Pagar ahora". Card and PayPal leave for the provider and come back here
 * with ?pago=<id>, which is checked with the server, never trusted. Yappy and
 * transfer are reported here and confirmed by Dale. With nothing configured
 * the page says so kindly and lets the owner carry on.
 *
 * Stripe, when it is on, is listed first and comes back with ?stripe=listo,
 * where the page waits for Stripe to confirm the card. A new signup whose
 * card is not confirmed yet and whose free trial is over sees only the card
 * step here (`CardLocked`); one still on its trial sees the optional one
 * (`CardTrial`), which charges nothing until the trial ends.
 */

export type PagoPageProps = {
  /** The attempt a provider sent the owner back with. */
  paymentId?: string;
  cancelled?: boolean;
  /** Arrived straight from signup: show the steps and continue to primer día. */
  fromSignup?: boolean;
  /** Back from Stripe Checkout (?stripe=listo): wait for Stripe to confirm the card. */
  stripeReturned?: boolean;
};

const NOT_READY_TEXT = 'Estamos activando los pagos. Te escribimos para completar tu pago.';
const CANCELLED_TEXT = 'Cancelaste el pago. Puedes intentarlo de nuevo cuando quieras.';
const PENDING_TEXT = 'Todavía no vemos tu pago. Si ya pagaste, espera un momento y revisa de nuevo.';
const LOAD_ERROR_TEXT = 'No pudimos cargar tu pago. Intenta de nuevo.';
const ADJUSTMENT_TEXT =
  'Cambiaste de plan después de tu último pago. Lo que ya pagaste sigue contando y no te cobramos dos veces los mismos meses. Te escribimos para ajustar el pago a tu plan nuevo.';

export function PagoPage({ paymentId, cancelled = false, fromSignup = false, stripeReturned = false }: PagoPageProps) {
  const navigate = useNavigate();
  const client = useQueryClient();
  const continueTo = fromSignup ? '/primer-dia' : '/membresia';
  const [reported, setReported] = useState<PaymentAttempt | null>(null);
  const [payAhead, setPayAhead] = useState(false);
  // A new signup waiting for Stripe to confirm its card sees only the card step.
  const session = useContext(SessionContext)?.session;
  const cardLocked = isCardLockedNow(session);
  // Still on the free trial with no card: saving one is optional and charges nothing yet.
  const onTrial = !cardLocked && !stripeReturned && isTrialRunning(session?.trialEndsAt);

  const billing = useQuery({ queryKey: BILLING_QUERY_KEY, queryFn: getBilling, retry: false, enabled: !cardLocked && !onTrial && !stripeReturned });
  const verify = useQuery({
    queryKey: ['billing-verify', paymentId],
    queryFn: () => verifyPayment(paymentId!),
    enabled: Boolean(paymentId) && !cancelled,
    retry: false,
    staleTime: Infinity,
  });

  // A settled return changes coverage and the latest attempt: read them again.
  useEffect(() => {
    if (verify.data && verify.data.status !== 'pendiente') void client.invalidateQueries({ queryKey: BILLING_QUERY_KEY });
  }, [verify.data, client]);

  const hosted = useMutationAttempt({
    mutationFn: (method: 'tarjeta' | 'paypal', key: string) => startCheckout({ method }, key),
    onSuccess: (result) => {
      if (!result.checkoutUrl) return;
      if (!isTrustedCheckoutUrl(result.checkoutUrl)) throw new Error('El enlace de pago no es válido. Intenta de nuevo.');
      window.location.assign(result.checkoutUrl);
    },
  });
  const report = useMutationAttempt({
    mutationFn: (input: { method: 'yappy' | 'transferencia'; reference: string }, key: string) => startCheckout(input, key),
    onSuccess: async (result) => {
      setReported(result.payment);
      await client.invalidateQueries({ queryKey: BILLING_QUERY_KEY });
    },
  });

  const stripe = useMutationAttempt({
    mutationFn: (input: StripeCheckoutInput, key: string) => startStripeCheckout(input, key),
  });

  const goOn = () => void navigate({ to: continueTo });
  // Result screens carry their own way on; only the checkout offers "Hacerlo después".
  const shell = (children: ReactNode, skippable = false) => (
    <PagoShell fromSignup={fromSignup} continueTo={continueTo} skippable={skippable}>
      {children}
    </PagoShell>
  );

  if (stripeReturned) return shell(<StripeReturn fromSignup={fromSignup} onContinue={goOn} />);
  if (cardLocked) return shell(<CardLocked cancelled={cancelled} />);
  if (onTrial && session?.trialEndsAt) return shell(<CardTrial trialEndsAt={session.trialEndsAt} cancelled={cancelled} />, true);
  if (billing.isPending || (verify.isFetching && !verify.data)) return shell(<LoadingState label="Cargando tu pago…" />);
  if (billing.isError) {
    if (isNotConfigured(billing.error)) return shell(<NotReadyPanel fromSignup={fromSignup} onContinue={goOn} />);
    return shell(
      <div className="pago-result">
        <ErrorBanner message={LOAD_ERROR_TEXT} />
        <button type="button" className="btn secondary" onClick={() => void billing.refetch()}>
          Reintentar
        </button>
      </div>,
    );
  }

  const view = billing.data;
  if (!view.quote) return shell(<NoFeePanel onContinue={goOn} />);
  const waiting = reported ?? (view.latest?.status === 'reportado' ? view.latest : null);
  if (waiting) return shell(<ReportedPanel attempt={waiting} fromSignup={fromSignup} onContinue={goOn} />);
  if (verify.data?.status === 'confirmado') return shell(<PaidPanel view={view} fromSignup={fromSignup} onContinue={goOn} />);
  if (view.coverage.status === 'al_dia' && !payAhead) {
    return shell(<UpToDatePanel view={view} onContinue={goOn} onPayAhead={() => setPayAhead(true)} />);
  }
  if (view.needsAdjustment) return shell(<AdjustmentPanel view={view} fromSignup={fromSignup} onContinue={goOn} />);
  if (view.methods.length === 0) return shell(<NotReadyPanel fromSignup={fromSignup} onContinue={goOn} />);

  const notice = checkoutNotice({ cancelled, verified: verify.data, latest: view.latest });
  const failure = hosted.error ?? report.error ?? stripe.error ?? verify.error;
  const quote = view.quote;
  return shell(
    <>
      <PagoIntro fromSignup={fromSignup} businessName={view.businessName} />
      {notice ? (
        <p className="pago-notice" role="status" data-tone={notice.tone}>
          {notice.text}
          {verify.data?.status === 'pendiente' ? (
            <button type="button" className="pago-notice-action" onClick={() => void verify.refetch()}>
              Revisar de nuevo
            </button>
          ) : null}
        </p>
      ) : null}
      {failure ? <ErrorBanner message={errorText(failure)} /> : null}
      <div className="pago-grid">
        <PagoSummary quote={view.quote} canChangePlan />
        <PagoMethods
          methods={view.methods}
          amountCents={view.quote.amountCents}
          busy={hosted.isPending || report.isPending || stripe.isPending}
          onHosted={(method) => hosted.mutate(method)}
          onReport={(method, reference) => report.mutate({ method, reference })}
          onStripe={() => stripe.mutate({ plan: quote.plan, cycle: quote.billingCycle })}
        />
      </div>
    </>,
    true,
  );
}

function isNotConfigured(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 503 || error.code === 'feature_unavailable');
}

function errorText(error: unknown): string {
  return error instanceof ApiError ? error.message : 'No pudimos completar eso ahora mismo. Intenta de nuevo.';
}

function checkoutNotice(input: {
  cancelled: boolean;
  verified: PaymentAttempt | undefined;
  latest: PaymentAttempt | null;
}): { tone?: 'danger'; text: string } | undefined {
  if (input.cancelled) return { text: CANCELLED_TEXT };
  if (input.verified?.status === 'pendiente') return { text: PENDING_TEXT };
  const attempt = input.verified ?? input.latest;
  return attempt ? attemptNotice(attempt) : undefined;
}

type PagoShellProps = { fromSignup: boolean; continueTo: string; skippable: boolean; children: ReactNode };

function PagoShell({ fromSignup, continueTo, skippable, children }: PagoShellProps) {
  return (
    <main className="pago">
      <header className="signup-top">
        <p className="brand" aria-hidden="true">
          <span className="brand-word">
            Dale<span className="dot">.</span>
          </span>
          <span className="brand-sub">Negocios</span>
        </p>
        {skippable ? (
          <p className="signup-top-alt">
            <Link to={continueTo}>{fromSignup ? 'Hacerlo después' : 'Volver'}</Link>
          </p>
        ) : null}
      </header>
      {children}
    </main>
  );
}

function PagoIntro({ fromSignup, businessName }: { fromSignup: boolean; businessName: string }) {
  return (
    <div className="pago-intro">
      {fromSignup ? <SignupSteps current="pago" withPayment /> : null}
      <h1>{fromSignup ? 'Completa tu pago' : 'Paga tu plan'}</h1>
      <p className="lede">
        {fromSignup
          ? `${businessName} ya está creado. Elige cómo pagar tu plan y listo.`
          : `Elige cómo pagar el plan de ${businessName}.`}
      </p>
    </div>
  );
}

function ReportedPanel({ attempt, fromSignup, onContinue }: { attempt: PaymentAttempt; fromSignup: boolean; onContinue: () => void }) {
  const method = attempt.method === 'yappy' || attempt.method === 'transferencia' ? METHOD_COPY[attempt.method].title : attempt.method;
  return (
    <ResultPanel
      glyph="clock"
      title="Recibimos tu pago"
      actions={
        <button type="button" className="btn" onClick={onContinue}>
          {fromSignup ? 'Ir a mi panel' : 'Volver a Membresía'}
        </button>
      }
    >
      <p className="pago-result-lede">Lo confirmamos en unas horas y te avisamos por correo.</p>
      <dl className="pago-details">
        {attempt.plan ? (
          <div>
            <dt>Plan</dt>
            <dd>{`${planLabel(attempt.plan)}, pago ${attempt.billingCycle === 'annual' ? 'anual' : 'mensual'}`}</dd>
          </div>
        ) : null}
        <div>
          <dt>Método</dt>
          <dd>{method}</dd>
        </div>
        {attempt.reference ? (
          <div>
            <dt>Referencia</dt>
            <dd>{attempt.reference}</dd>
          </div>
        ) : null}
        <div>
          <dt>Monto</dt>
          <dd>{formatWholeDollars(attempt.amountCents)}</dd>
        </div>
      </dl>
    </ResultPanel>
  );
}

function PaidPanel({ view, fromSignup, onContinue }: { view: BillingView; fromSignup: boolean; onContinue: () => void }) {
  return (
    <ResultPanel
      glyph="check"
      title="Pago confirmado"
      actions={
        <button type="button" className="btn" onClick={onContinue}>
          {fromSignup ? 'Continuar' : 'Volver a Membresía'}
        </button>
      }
    >
      <p className="pago-result-lede">
        {view.quote ? `Tu plan ${planLabel(view.quote.plan)} de Dale quedó pagado. Te enviamos la confirmación por correo.` : 'Tu plan de Dale quedó pagado.'}
      </p>
      {view.coverage.lastCoveredMonth ? <p className="pago-result-until">{paidUntilLine(view.coverage.lastCoveredMonth)}</p> : null}
    </ResultPanel>
  );
}

function UpToDatePanel({ view, onContinue, onPayAhead }: { view: BillingView; onContinue: () => void; onPayAhead: () => void }) {
  return (
    <ResultPanel
      glyph="check"
      title="Tu plan está al día"
      actions={
        <>
          <button type="button" className="btn" onClick={onContinue}>
            Continuar
          </button>
          <button type="button" className="btn ghost" onClick={onPayAhead}>
            Pagar por adelantado
          </button>
        </>
      }
    >
      {view.coverage.lastCoveredMonth ? <p className="pago-result-until">{paidUntilLine(view.coverage.lastCoveredMonth)}</p> : null}
    </ResultPanel>
  );
}

/** Paid months stop short of the plan on file: no new checkout until Dale settles the difference (no proration). */
function AdjustmentPanel({ view, fromSignup, onContinue }: { view: BillingView; fromSignup: boolean; onContinue: () => void }) {
  return (
    <ResultPanel
      glyph="info"
      title="Tu plan cambió"
      actions={
        <button type="button" className="btn" onClick={onContinue}>
          {fromSignup ? 'Continuar' : 'Volver a Membresía'}
        </button>
      }
    >
      <p className="pago-result-lede">{ADJUSTMENT_TEXT}</p>
      {view.coverage.lastCoveredMonth ? <p className="pago-result-until">{paidUntilLine(view.coverage.lastCoveredMonth)}</p> : null}
    </ResultPanel>
  );
}

function NotReadyPanel({ fromSignup, onContinue }: { fromSignup: boolean; onContinue: () => void }) {
  return (
    <ResultPanel
      glyph="info"
      title={fromSignup ? 'Tu negocio quedó creado' : 'Pagos en camino'}
      actions={
        <button type="button" className="btn" onClick={onContinue}>
          Continuar
        </button>
      }
    >
      <p className="pago-result-lede">{NOT_READY_TEXT}</p>
    </ResultPanel>
  );
}

function NoFeePanel({ onContinue }: { onContinue: () => void }) {
  return (
    <ResultPanel
      glyph="info"
      title="Tu plan no tiene mensualidad"
      actions={
        <button type="button" className="btn" onClick={onContinue}>
          Continuar
        </button>
      }
    >
      <p className="pago-result-lede">Pagas solo por cada reserva que recibes. No hay nada que pagar aquí.</p>
    </ResultPanel>
  );
}
